import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Linkedin, Mail } from 'lucide-react';
import { SEO } from '@/components/SEO';

const TEAM_PHOTO = "https://customer-assets.emergentagent.com/job_cxo-placement/artifacts/my1x077o_ChatGPT%20Image%20Mar%208%2C%202026%2C%2010_13_56%20AM.png";

const team = [
  {
    name: 'Priyaa V Jain',
    role: 'Managing Partner',
    specializations: ['Client Relationships', 'Executive Hiring', 'Business Strategy'],
    bio: 'MBA with deep experience across banking, insurance, and executive hiring. Priyaa leads client relationships and the executive search vertical, helping organisations identify exceptional finance and leadership talent.',
    details: '15+ years in financial services | Previously with Kotak, HDFC and leading financial institutions | Specialist in CFO and senior finance hiring',
    linkedin: 'https://www.linkedin.com/in/priyaa-v-jain-0707b312/',
  },
  {
    name: 'Priyanka Golchha',
    role: 'Partner & Chartered Accountant',
    specializations: ['Accounting', 'Tax & Compliance', 'Micro CFO'],
    bio: 'A qualified Chartered Accountant specialising in accounting, taxation, and compliance. Priyanka leads the technical and advisory side of Ratnatrraya, ensuring clients receive structured financial guidance and regulatory clarity. She works closely with founders as a Micro CFO, helping them move from confusion to financial clarity and control.',
    details: 'Qualified CA (ICAI) | Expertise across startups, SMEs, and NGOs | Deep experience in GST, direct tax, and audit',
    linkedin: 'https://www.linkedin.com/in/priyanka-golchha-baa817267/',
  },
];

export default function TeamPage() {
  return (
    <div className="pt-20" data-testid="team-page">
      <SEO 
        title="Our Team - Expert MBAs & Chartered Accountants"
        path="/team"
        description="Meet the Ratnatrraya Consulting team — MBA and Chartered Accountant professionals providing Micro CFO, accounting, tax, and CXO hiring services for small businesses across India."
        keywords="Ratnatrraya team, Priyaa V Jain, Priyanka Golchha, CA firm team, Micro CFO team, Hyderabad consultants"
      />
      {/* Hero */}
      <section className="py-20 md:py-28 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="text-sm font-medium tracking-widest text-[#E84C3D] uppercase mb-4 block">Our Team</span>
          <h1 className="font-secondary text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Meet the Founders
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Two complementary leaders with a shared vision — bringing right vision, right knowledge, 
            and right action to every client engagement.
          </p>
        </div>
      </section>

      {/* Team Photo Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="relative">
            <div className="absolute -inset-4 bg-[#E84C3D]/5 rounded-3xl transform -rotate-1"></div>
            <img 
              src={TEAM_PHOTO} 
              alt="Priyaa V Jain and Priyanka Golchha - Ratnatrraya Consulting LLP Partners"
              className="relative rounded-2xl w-full shadow-xl"
            />
          </div>
        </div>
      </section>

      {/* Team Members Details */}
      <section className="py-20 md:py-28 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {team.map((member, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg"
                data-testid={`team-member-${index}`}
              >
                <span className="text-[#E84C3D] font-medium text-sm mb-2 block">{member.role}</span>
                <h2 className="font-secondary text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                  {member.name}
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  {member.bio}
                </p>
                
                {/* Additional Details */}
                <p className="text-sm text-slate-500 mb-5 italic">
                  {member.details}
                </p>
                
                <div className="mb-5">
                  <h4 className="font-semibold text-slate-900 mb-2 text-sm">Specializations</h4>
                  <div className="flex flex-wrap gap-2">
                    {member.specializations.map((spec, i) => (
                      <span 
                        key={i}
                        className="px-3 py-1.5 bg-[#F9F9F7] rounded-full text-xs text-slate-700"
                      >
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>

                {/* LinkedIn Button - Prominent */}
                <a 
                  href={member.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0A66C2] text-white rounded-lg hover:bg-[#004182] transition-colors font-medium text-sm"
                  data-testid={`linkedin-${member.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Linkedin size={18} />
                  Connect on LinkedIn
                </a>
              </div>
            ))}
          </div>

          {/* Extended Team Note */}
          <div className="mt-12 bg-white p-6 rounded-xl text-center">
            <p className="text-slate-600">
              <strong className="text-slate-900">Our extended team</strong> includes experienced accountants, tax specialists, and research associates 
              who work closely with the founders to deliver comprehensive support to every client.
            </p>
          </div>
        </div>
      </section>

      {/* Trust Elements */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="p-6">
              <p className="text-sm font-semibold text-[#E84C3D] mb-1">Our Expertise</p>
              <p className="text-slate-600 text-sm">Team includes qualified MBAs, CAs, CS, tax and legal experts, and research associates</p>
            </div>
            <div className="p-6">
              <p className="text-sm font-semibold text-[#E84C3D] mb-1">Experience</p>
              <p className="text-slate-600 text-sm">30+ years of combined advisory experience across SMEs, NGOs, and corporates</p>
            </div>
            <div className="p-6">
              <p className="text-sm font-semibold text-[#E84C3D] mb-1">Locations</p>
              <p className="text-slate-600 text-sm">Serving clients across Indian States located in Metros or small towns</p>
            </div>
          </div>
        </div>
      </section>

      {/* Work With Us */}
      <section className="py-20 md:py-28 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-secondary text-3xl md:text-4xl font-semibold mb-4">
            Work With Us
          </h2>
          <p className="text-slate-400 mb-6">
            We're always looking for talented professionals who share our values of excellence, 
            integrity, and client-first thinking.
          </p>
          <p className="text-sm text-slate-500 mb-8">
            Open to: Experienced CAs, Tax consultants, Finance professionals, and Research associates
          </p>
          <a href="mailto:support@ratnatrraya.consulting">
            <Button className="bg-[#E84C3D] hover:bg-[#C0392B] text-white px-6 py-5">
              <Mail className="mr-2 h-4 w-4" />
              Send Your Resume
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
